import { FC, useMemo } from 'react';

import { multicreationFiltersElementShownRaw } from '@hh.ru/analytics-js-events/build/clickme/employer/auction/multicreation/multicreation_filters_element_shown';
import { createStaticDataProvider } from '@hh.ru/magritte-common-data-provider';

import useTrls from 'hooks/translation/useTrls';

import { SingleFilter as CommonSingleFilter } from 'components/common/SingleFilter/SingleFilter';
import { fetchVacancySearch } from 'components/MultiCreationAuction/VacanciesFiltersPanel/fetchVacancySearch';

import {
    applyMultiCreationFilters,
    FilterOption,
    resetMultiCreationSelectedFiltersToApplied,
    setMultiCreationSelectedFiltersByFilterKey,
    VacancyFilterKeys,
} from 'store/models/multiCreation';
import { useDispatch, useSelector } from 'store/utils';

interface SingleFilterProps {
    filterKey: VacancyFilterKeys;
    filterData?: FilterOption[];
}

const TrlKeys = {
    apply: 'MultiCreationAuction.SingleFilter.apply',
    discard: 'MultiCreationAuction.SingleFilter.discard',
    placeholder: {
        areas: 'MultiCreationAuction.SingleFilter.placeholder.areas',
        users: 'MultiCreationAuction.SingleFilter.placeholder.users',
    },
};

export const SingleFilter: FC<SingleFilterProps> = ({ filterData, filterKey }) => {
    const appliedOptions = useSelector(({ multiCreation }) => multiCreation.appliedFilters)[filterKey];
    const selectedOptions = useSelector(({ multiCreation }) => multiCreation.selectedFilters)[filterKey];
    const vacanciesIsLoading = useSelector(({ multiCreation }) => multiCreation.vacanciesIsLoading);

    const staticProvider = useMemo(() => createStaticDataProvider(filterData ?? []), [filterData]);

    const trls = useTrls();
    const dispatch = useDispatch();

    if (!filterData) {
        return null;
    }

    const handleApplyFilter = () => {
        void dispatch(fetchVacancySearch({ [filterKey]: selectedOptions }));
        void dispatch(applyMultiCreationFilters(null));
    };

    const handleDiscardFilter = () => {
        void dispatch(fetchVacancySearch({ [filterKey]: [] }));
        void dispatch([setMultiCreationSelectedFiltersByFilterKey({ filterKey }), applyMultiCreationFilters(null)]);
    };

    const handleSelectOption = (newValues: FilterOption[]) => {
        dispatch(setMultiCreationSelectedFiltersByFilterKey({ filterKey, newValues }));
    };

    const handleDropClose = () => {
        dispatch(resetMultiCreationSelectedFiltersToApplied(null));
    };

    return (
        <CommonSingleFilter
            name={filterKey}
            placeholder={trls(TrlKeys.placeholder[filterKey])}
            appliedValues={appliedOptions}
            value={selectedOptions}
            isLoading={vacanciesIsLoading}
            dataProvider={staticProvider}
            applyLabel={trls(TrlKeys.apply)}
            discardLabel={trls(TrlKeys.discard)}
            dataQaPrefix={`single-filter-${filterKey}`}
            onApply={handleApplyFilter}
            onClear={handleDiscardFilter}
            onChange={handleSelectOption}
            onSelectOption={handleSelectOption}
            onDropClose={handleDropClose}
            onBottomSheetClose={handleDropClose}
            onDropOpen={() =>
                multicreationFiltersElementShownRaw({
                    elementName: `multicreation_filter_${filterKey}`,
                })
            }
        />
    );
};
